import { FC, useEffect, useMemo, useRef } from "react";
import { useShallow } from "zustand/react/shallow";

import styled from "styled-components/macro";

import { WidgetComponentType } from "models/screenshot";

import { useScreenshotDispatch } from "components/screenshot/hooks";
import { useScreenshotContext } from "components/screenshot/hooks/useScreenshotContext";

import { Portal } from "../ui";
import { useXdaSettingsStore } from "./context";
import { selectXdaSettings } from "./context/xdaSettingsSelectors";

type ScreenshotT = {
  containerId: string;
  /** Which viewer this overlay belongs to. See `VisScreenshotToggle`. */
  screenshotWidgetId: string;
};

const Screenshot: FC<ScreenshotT> = ({ containerId, screenshotWidgetId }) => {
  // context
  const { settings, widget } = useScreenshotContext();
  const xdaSettings = useXdaSettingsStore(useShallow(selectXdaSettings));

  // dispatch
  const screenshotDispatch = useScreenshotDispatch();

  // ref
  const overlayRef = useRef<HTMLDivElement>(null);

  // derived state
  const screenshotContainer = document.getElementById(containerId);

  const screenshotOverlayVisible = useMemo(() => {
    return widget?.widgetId === screenshotWidgetId;
  }, [widget, screenshotWidgetId]);

  useEffect(() => {
    if (screenshotOverlayVisible && screenshotContainer) {
      screenshotDispatch({
        payload: {
          screenshotOverlay: overlayRef.current,
          // The kind of viewer, not which one: every XDA reports its settings.
          widgetState:
            widget?.widgetComponentType === WidgetComponentType.XdaViewer
              ? { settings: xdaSettings }
              : undefined
        }
      });
    }
  }, [
    screenshotOverlayVisible,
    screenshotContainer,
    screenshotDispatch,
    xdaSettings,
    widget?.widgetId
  ]);

  if (!screenshotOverlayVisible || !screenshotContainer) return null;

  const screenshotBounds = {
    width: settings?.width || 1152,
    height: settings?.height || 681
  };

  return (
    <Portal container={screenshotContainer}>
      <Overlay
        ref={overlayRef}
        width={screenshotBounds.width}
        height={screenshotBounds.height}></Overlay>
    </Portal>
  );
};

export default Screenshot;

const Overlay = styled.div`
  position: absolute;
  top: -2px;
  left: -2px;
  width: calc(${(p) => p.width}px + 4px);
  height: calc(${(p) => p.height}px + 4px);
  border: 2px solid rgb(255, 208, 0);
  z-index: 1;
  pointer-events: none;
`;
